/*
# -----------------------------------------------------------------------------
#	Project: HSE Genius®
#	Client ID: 834215
#	Registration Code: IT-2025-ZR319-MQ744
#	Author: KaimakiWeb
#	Website: https://kaimakiweb.com
#	Copyright (c) 2024-2026 Kaimaki Web. All rights reserved.
#
#	This software and its source code are the property of KaimakiWeb.
#	Unauthorized copying, modification, distribution, or use of this software,
#	via any medium, is strictly prohibited unless prior written permission
#	is obtained from KaimakiWeb.
# -----------------------------------------------------------------------------
*/

/**
 * Per-session table state written straight to localStorage by the parsed-files
 * table (see components/pages/parsed-files/parsed-files-table).
 *
 * These keys are session state, not preferences: they hold the lock/validating
 * flags and sets of document IDs belonging to the company the user was working
 * in. They were previously only ever overwritten, never removed — so a browser
 * that got stuck with `parsedFiles_tableIsLocked=true` (e.g. during a run that
 * failed before the unlock write) kept a read-only table across logout, login
 * and every deploy, which reads as a bug that "came back". On a shared machine
 * the row-ID sets also carried over from one account to the next.
 *
 * Deliberately NOT listed here (genuine user preferences that should survive a
 * logout): `mode` (theme), `tableVisibleColumns`, `tableFilters_*`,
 * `tableColor_*`, and the i18next language key.
 */
const SESSION_STATE_KEYS = [
  'validating',
  'parsedFiles_tableIsLocked',
  'parsedFiles_lockedRows',
  'parsedFiles_expandedRows',
  'validation_tableIsLocked',
  'validation_lockedRows',
  'validation_expandedRows',
  'taskId',
];

/**
 * Bump when the shape or semantics of the keys above change, or to force a
 * one-time purge on every existing client at the next page load.
 */
const SESSION_STATE_VERSION = '1';

const VERSION_KEY = 'sessionStateVersion';

/**
 * Drop all per-session table state. Safe to call when nothing is stored.
 * localStorage access is guarded because it throws in Safari private mode and
 * when a browser blocks storage for the origin — a failure here must never
 * take down logout.
 */
export const clearSessionState = () => {
  try {
    SESSION_STATE_KEYS.forEach((key) => localStorage.removeItem(key));
  } catch (_) {
    // Storage unavailable — nothing was persisted, so nothing to clean up.
  }
};

/**
 * One-time purge for clients carrying state written by an older build.
 *
 * Called at boot, before React renders. Without this, only users who happen to
 * log out would shed a stuck lock flag; a client that stays signed in would
 * keep it indefinitely. The cost is that a reload during an in-flight
 * validation clears the lock once — the table re-derives it from the current
 * row data, so the user just sees an unlocked table instead of a stuck one.
 */
export const purgeStaleSessionState = () => {
  try {
    if (localStorage.getItem(VERSION_KEY) === SESSION_STATE_VERSION) return;
    clearSessionState();
    localStorage.setItem(VERSION_KEY, SESSION_STATE_VERSION);
  } catch (_) {
    // Storage unavailable — no stale state can exist.
  }
};

export { SESSION_STATE_KEYS, SESSION_STATE_VERSION };
